import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../store/slices/authSlice";
import { UI } from "../constants";

// Global flag to pause inactivity timer (e.g., during session recording)
let isInactivityPaused = false;

// Custom event to notify timer to restart after resume
const INACTIVITY_RESUME_EVENT = "inactivity-timer-resume";

/**
 * Pause the inactivity timer globally (e.g., during session recording)
 */
export const pauseInactivityTimer = () => {
  isInactivityPaused = true;
};

/**
 * Resume the inactivity timer globally and restart the timer
 */
export const resumeInactivityTimer = () => {
  isInactivityPaused = false;
  // Dispatch custom event to restart the timer
  document.dispatchEvent(new CustomEvent(INACTIVITY_RESUME_EVENT));
};

/**
 * Check if inactivity timer is paused
 */
export const isInactivityTimerPaused = () => isInactivityPaused;

// How long user must be idle before we log "interrupted after N min" (ms)
const LOG_IDLE_THRESHOLD_MS = 60 * 1000; // 1 minute
// Interval for the "still idle" periodic log (ms)
const LOG_IDLE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to automatically log out user after specified minutes of inactivity
 * @param inactivityMinutes - Number of minutes of inactivity before auto logout
 *   (default: UI.INACTIVITY_TIMEOUT_MINUTES, set to 0 to disable)
 * @param onLogout - Optional callback that performs the full logout sequence.
 *   When provided it is called instead of a bare dispatch(logout()), ensuring
 *   cookies, leadership, and refresh timers are all cleaned up correctly.
 */

export const useInactivityTimer = (
  inactivityMinutes: number = UI.INACTIVITY_TIMEOUT_MINUTES,
  onLogout?: () => void,
) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Timestamp when the current idle period started (null = user is active)
  const idleStartRef = useRef<number | null>(null);
  // setInterval handle for the periodic "still idle" log
  const idleIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimeMs = inactivityMinutes * 60 * 1000;

  // Start tracking an idle period
  const startIdleTracking = useCallback(() => {
    if (idleStartRef.current !== null) return; // already tracking
    idleStartRef.current = Date.now();

    // Log every 5 min while the user stays idle
    idleIntervalRef.current = setInterval(() => {
      if (idleStartRef.current === null) return;
      const idleSec = Math.round((Date.now() - idleStartRef.current) / 1000);
      const idleMin = (idleSec / 60).toFixed(1);
      console.log(
        `[InactivityTimer] User still idle — total idle time: ${idleMin} min (${idleSec}s). Auto-logout in ${(inactivityMinutes - parseFloat(idleMin)).toFixed(1)} min.`,
      );
    }, LOG_IDLE_INTERVAL_MS);
  }, [inactivityMinutes]);

  // Stop tracking the idle period, optionally logging the interruption cause
  const stopIdleTracking = useCallback((cause?: string) => {
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = null;
    }

    if (idleStartRef.current !== null) {
      const idleSec = Math.round((Date.now() - idleStartRef.current) / 1000);
      // Only log if the user was idle long enough to be noteworthy (≥ 1 min)
      if (idleSec * 1000 >= LOG_IDLE_THRESHOLD_MS) {
        console.log(
          `[InactivityTimer] Inactivity interrupted after ${(idleSec / 60).toFixed(1)} min (${idleSec}s). Cause: ${cause ?? "unknown"}.`,
        );
      }
      idleStartRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    if (isInactivityPaused) {
      return;
    }
    stopIdleTracking();
    console.log(
      `[InactivityTimer] Auto-logout triggered after ${inactivityMinutes} min of inactivity.`,
    );
    if (onLogout) {
      onLogout();
    } else {
      dispatch(logout());
    }
  }, [dispatch, onLogout, inactivityMinutes, stopIdleTracking]);

  const resetTimer = useCallback(
    (cause?: string) => {
      const wasIdle = timeoutRef.current !== null;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (isInactivityPaused) {
        return;
      }

      // If activity interrupted an ongoing idle period, log it
      if (wasIdle) {
        stopIdleTracking(cause);
      }

      // Start the inactivity countdown again
      timeoutRef.current = setTimeout(() => {
        handleLogout();
      }, inactivityTimeMs);

      // Begin idle tracking (no-op if already tracking)
      startIdleTracking();
    },
    [inactivityTimeMs, handleLogout, startIdleTracking, stopIdleTracking],
  );

  useEffect(() => {
    if (inactivityMinutes <= 0) {
      return;
    }

    const events: (keyof DocumentEventMap)[] = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Initialize timer and start idle tracking on mount
    resetTimer("mount");

    // Each activity event passes its type as the interruption cause
    const handlers = events.map((event) => {
      const handler = () => resetTimer(event);
      document.addEventListener(event, handler);
      return { event, handler };
    });

    const handleResume = () => resetTimer(INACTIVITY_RESUME_EVENT);
    document.addEventListener(INACTIVITY_RESUME_EVENT, handleResume);

    return () => {
      handlers.forEach(({ event, handler }) =>
        document.removeEventListener(event, handler),
      );
      document.removeEventListener(INACTIVITY_RESUME_EVENT, handleResume);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
      }
    };
  }, [resetTimer, inactivityMinutes]);
};
